import { ChevronRight, Eye, RefreshCw } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import TabsComponent from "../../../Components/Tab";
import { useAppDispatch } from "../../../Store/Hooks";
import {
  fetchMetricsConfigCoverageAudit,
  fetchMetricsOrgConfiguration,
} from "../../../Store/Reducers/MetricsSlice";
import {
  getCategoryById,
  getCategoryByLabel,
} from "../../constants/kpiCategories";

type CoverageView = "gaps" | "matrix";
type StatusLabel = "Active" | "Partial data" | "No data" | "Disabled" | string;

interface MetricRow {
  metric_id: string;
  metric_name: string;
  status_label: StatusLabel;
  raw_coverage_value?: string | number | null;
}

interface CategoryCard {
  category_key: string;
  category_display_name: string;
  metrics: MetricRow[];
  activeCt: number;
  partialCt: number;
  noDataCt: number;
  weight?: number;
}

interface DataCoverageAuditPanelProps {
  scopedAccountIds?: string[];
  onBack?: () => void;
  /** When true, renders inside Data Hub without outer page padding/card chrome. */
  embedded?: boolean;
}

const METRIC_STATUS_DESCRIPTION: Record<string, string> = {
  "No data": "No data source connected — contact your admin",
  "Partial data": "Available for some accounts only",
  Active: "Data ingested and contributing to scores",
};

const AUDIT_VIEW_TABS = [
  { id: "gaps", label: "Critical Gaps" },
  { id: "matrix", label: "Coverage Matrix" },
];

const STATUS_KEY_ITEMS: Array<{ label: string; className: string; hint: string }> = [
  { label: "Active", className: "border-emerald-100 bg-emerald-50 text-emerald-700", hint: "Data ingested" },
  { label: "Partial data", className: "border-amber-100 bg-amber-50 text-amber-700", hint: "Available for some accounts" },
  { label: "No data", className: "border-slate-200 bg-slate-100 text-slate-600", hint: "Metric defined, not connected" },
  { label: "Inactive", className: "border-rose-100 bg-rose-50 text-rose-700", hint: "Excluded by admin" },
];

const toReadableLabel = (value?: string) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const normalizeKey = (value?: string) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const isNoDataStatus = (status?: string) => {
  const s = String(status || "").toLowerCase();
  return s === "missing" || s === "no_data" || s === "nodata";
};

const isPartialStatus = (status?: string) => String(status || "").toLowerCase() === "partial";

const rollupStatusLabel = (complete: number, partial: number, missing: number): StatusLabel => {
  if (missing > 0 && partial === 0 && complete === 0) return "No data";
  if (partial > 0) return "Partial data";
  if (complete > 0) return "Active";
  return "No data";
};

const configStatusLabel = (rawCoverage: number | null | undefined): StatusLabel => {
  if (rawCoverage === null || rawCoverage === undefined || Number.isNaN(rawCoverage)) {
    return "No data";
  }
  if (rawCoverage === 100) return "Active";
  if (rawCoverage < 100) return "Partial data";
  return "No data";
};

const statusPillClass = (statusLabel: string) => {
  if (statusLabel === "Active") return "bg-emerald-100 text-emerald-800";
  if (statusLabel === "Partial data") return "bg-amber-100 text-amber-900";
  if (statusLabel === "No data") return "bg-slate-100 text-slate-600";
  return "bg-rose-100 text-rose-800";
};

const formatCoverageText = (rawCoverage: string | number | null | undefined) => {
  if (rawCoverage === null || rawCoverage === undefined || String(rawCoverage).trim() === "") {
    return "";
  }
  return `${rawCoverage}% accts`;
};

const getCategoryBarWidth = (cat: CategoryCard) => {
  const total = cat.metrics.length || 1;
  return Math.max(4, Math.min(100, ((cat.activeCt + cat.partialCt) / total) * 100));
};

const DataCoverageAuditPanel: React.FC<DataCoverageAuditPanelProps> = ({
  scopedAccountIds = [],
  onBack,
  embedded = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<CoverageView>("gaps");
  const [payload, setPayload] = useState<any>(null);
  const [orgConfiguration, setOrgConfiguration] = useState<any>(null);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const matrixScrollRef = useRef<HTMLDivElement | null>(null);
  const [isMatrixOverflowing, setIsMatrixOverflowing] = useState(true);
  const dispatch = useAppDispatch();

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [auditRes, orgConfig] = await Promise.all([
        dispatch(fetchMetricsConfigCoverageAudit()).unwrap(),
        dispatch(fetchMetricsOrgConfiguration()).unwrap(),
      ]);
      setPayload(auditRes);
      setOrgConfiguration(orgConfig || null);
    } catch (e: any) {
      setError(e?.message || "Failed to load data coverage audit.");
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  const content = payload?.content;
  const diagnostics = content?.expected_filter_diagnostics;

  const overrideCategories = useMemo(() => {
    const cats =
      orgConfiguration?.config?.categories ||
      (orgConfiguration as { config_override?: { categories?: unknown } })
        ?.config_override?.categories ||
      orgConfiguration?.effective_config?.categories;
    return Array.isArray(cats) ? cats : [];
  }, [orgConfiguration]);

  const scopedSet = useMemo(
    () => new Set((scopedAccountIds || []).map(id => String(id).trim()).filter(Boolean)),
    [scopedAccountIds]
  );

  const metricRollups = useMemo(() => {
    const all = Array.isArray(content?.metric_rollups) ? content.metric_rollups : [];
    if (!scopedSet.size) return all;
    const filtered = all.filter((row: any) => scopedSet.has(String(row?.account_id || "").trim()));
    return filtered.length ? filtered : all;
  }, [content?.metric_rollups, scopedSet]);

  const metricNameLookup = useMemo(() => {
    const byKey: Record<string, { metric_id: string; metric_name: string }> = {};
    metricRollups.forEach((accountRow: any) => {
      (accountRow?.metrics || []).forEach((m: any) => {
        const idKey = normalizeKey(m.metric_id);
        const nameKey = normalizeKey(m.metric_name);
        const entry = { metric_id: String(m.metric_id), metric_name: m.metric_name || m.metric_id };
        if (idKey) byKey[idKey] = entry;
        if (nameKey) byKey[nameKey] = entry;
      });
    });
    return byKey;
  }, [metricRollups]);

  const categoryCards = useMemo((): CategoryCard[] => {
    const auditCategoryRollups = Array.isArray(content?.category_rollups)
      ? content.category_rollups
      : [];
    const auditCategoryMap = new Map<string, any>(
      auditCategoryRollups.map((c: any) => [normalizeKey(c?.category_key), c])
    );

    if (overrideCategories.length) {
      return overrideCategories.map((cat: any) => {
        const categoryKeyRaw = String(cat?.name || "");
        const auditCat = auditCategoryMap.get(normalizeKey(categoryKeyRaw));
        const metrics: MetricRow[] = (Array.isArray(cat?.metrics) ? cat.metrics : []).map(
          (metric: any) => {
            const metricNameRaw = String(metric?.name || "");
            const match =
              metricNameLookup[normalizeKey(metricNameRaw)] ||
              metricNameLookup[normalizeKey(metric?.metric_name)];
            const rawCoverageValue =
              metric?.coverage ??
              metric?.coverage_detail?.coverage_pct_total_accounts ??
              metric?.coverage_detail?.coverage_pct_timeseries_accounts ??
              null;
            const statusLabel = configStatusLabel(
              rawCoverageValue === null || rawCoverageValue === undefined || rawCoverageValue === ""
                ? null
                : Number(rawCoverageValue)
            );
            return {
              metric_id: metricNameRaw || match?.metric_id || "unknown_metric",
              metric_name: match?.metric_name || toReadableLabel(metricNameRaw),
              status_label: statusLabel,
              raw_coverage_value: rawCoverageValue,
            };
          }
        );

        return {
          category_key: categoryKeyRaw,
          category_display_name:
            auditCat?.category_display_name || toReadableLabel(categoryKeyRaw),
          metrics,
          activeCt: metrics.filter(m => m.status_label === "Active").length,
          partialCt: metrics.filter(m => m.status_label === "Partial data").length,
          noDataCt: metrics.filter(m => m.status_label === "No data").length,
          weight: Number(cat?.weight || 0),
        };
      });
    }

    const rollupMetricsByCategory = new Map<string, any[]>();
    metricRollups.forEach((accountRow: any) => {
      (accountRow?.metrics || []).forEach((m: any) => {
        const catKey = normalizeKey(m.category_key);
        if (!rollupMetricsByCategory.has(catKey)) rollupMetricsByCategory.set(catKey, []);
        rollupMetricsByCategory.get(catKey)!.push(m);
      });
    });

    return auditCategoryRollups.map((cat: any) => {
      const catKey = normalizeKey(cat.category_key);
      const rollupRows = rollupMetricsByCategory.get(catKey) || [];
      const byMetricId = new Map<string, { complete: number; partial: number; missing: number; name: string }>();

      rollupRows.forEach((m: any) => {
        const key = String(m.metric_id || "");
        if (!key) return;
        if (!byMetricId.has(key)) {
          byMetricId.set(key, { complete: 0, partial: 0, missing: 0, name: m.metric_name || key });
        }
        const bucket = byMetricId.get(key)!;
        const status = String(m.status || "").toLowerCase();
        if (isNoDataStatus(status)) bucket.missing += 1;
        else if (isPartialStatus(status)) bucket.partial += 1;
        else bucket.complete += 1;
      });

      const metrics: MetricRow[] = Array.from(byMetricId.entries()).map(([metric_id, counts]) => ({
        metric_id,
        metric_name: counts.name,
        status_label: rollupStatusLabel(counts.complete, counts.partial, counts.missing),
      }));

      return {
        category_key: cat.category_key,
        category_display_name: cat.category_display_name || toReadableLabel(cat.category_key),
        metrics,
        activeCt: metrics.filter(m => m.status_label === "Active").length,
        partialCt: metrics.filter(m => m.status_label === "Partial data").length,
        noDataCt: metrics.filter(m => m.status_label === "No data").length,
      };
    });
  }, [content?.category_rollups, metricRollups, metricNameLookup, overrideCategories]);

  const matrixColumns = useMemo(() => {
    const byId: Record<string, { metric_id: string; metric_name: string; category_display_name: string }> = {};
    metricRollups.forEach((accountRow: any) => {
      (accountRow?.metrics || []).forEach((m: any) => {
        if (!m?.metric_id || byId[m.metric_id]) return;
        byId[m.metric_id] = {
          metric_id: m.metric_id,
          metric_name: m.metric_name || m.metric_id,
          category_display_name: m.category_display_name || "Other",
        };
      });
    });
    return Object.values(byId).sort((a, b) => {
      const catCmp = a.category_display_name.localeCompare(b.category_display_name);
      return catCmp !== 0 ? catCmp : a.metric_name.localeCompare(b.metric_name);
    });
  }, [metricRollups]);

  const matrixCategoryGroups = useMemo(() => {
    const groups: Array<{ category: string; count: number }> = [];
    let currentCategory = "";
    matrixColumns.forEach(col => {
      if (col.category_display_name !== currentCategory) {
        currentCategory = col.category_display_name;
        groups.push({ category: currentCategory, count: 1 });
      } else if (groups.length) {
        groups[groups.length - 1].count += 1;
      }
    });
    return groups;
  }, [matrixColumns]);

  useEffect(() => {
    if (view !== "matrix") return;
    const updateOverflowState = () => {
      const el = matrixScrollRef.current;
      if (!el) return;
      setIsMatrixOverflowing(el.scrollWidth > el.clientWidth + 1);
    };
    updateOverflowState();
    window.addEventListener("resize", updateOverflowState);
    return () => window.removeEventListener("resize", updateOverflowState);
  }, [view, matrixColumns.length, metricRollups.length]);

  const orderedCategoryCards = useMemo(() => {
    const order = Array.isArray(diagnostics?.effective_included_categories)
      ? diagnostics.effective_included_categories.map((c: string) => normalizeKey(c))
      : [];
    if (!order.length) return categoryCards;
    const rank = new Map<string, number>(
      order.map((cat: string, idx: number) => [cat, idx] as [string, number])
    );
    return [...categoryCards].sort((a, b) => {
      const aKey = normalizeKey(a.category_key || a.category_display_name);
      const bKey = normalizeKey(b.category_key || b.category_display_name);
      const ar = rank.get(aKey) ?? Number.MAX_SAFE_INTEGER;
      const br = rank.get(bKey) ?? Number.MAX_SAFE_INTEGER;
      if (ar !== br) return ar - br;
      return a.category_display_name.localeCompare(b.category_display_name);
    });
  }, [categoryCards, diagnostics?.effective_included_categories]);

  const readinessSummary = useMemo(() => {
    const metricRows = orderedCategoryCards.flatMap(cat => cat.metrics);
    let active = 0;
    let partial = 0;
    let noData = 0;
    metricRows.forEach(m => {
      if (m.status_label === "Active") active += 1;
      else if (m.status_label === "Partial data") partial += 1;
      else if (m.status_label === "No data") noData += 1;
    });
    const readiness = metricRows.length
      ? Math.round(((active + partial * 0.5) / metricRows.length) * 100)
      : Math.round(Number(content?.readiness_percentage || 0));
    return { active, partial, noData, readiness };
  }, [orderedCategoryCards, content?.readiness_percentage]);

  const toggleCategory = (categoryKey: string) => {
    setExpandedCategories(prev => ({ ...prev, [categoryKey]: !prev[categoryKey] }));
  };

  const outerClass = embedded ? "" : "px-4 pb-6";
  const cardClass = embedded ? "" : "overflow-hidden";
  const stateWrapClass = embedded
    ? "py-4 text-sm text-slate-600"
    : "mx-4 my-4 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600";

  if (loading && !content) {
    return <div className={stateWrapClass}>Loading Data Coverage Audit...</div>;
  }

  if (error && !content) {
    return (
      <div
        className={
          embedded
            ? "py-4 text-sm text-rose-700"
            : "mx-4 my-4 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"
        }
      >
        {error}
      </div>
    );
  }

  return (
    <div className={outerClass}>
      <div className={cardClass}>
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Data Coverage Audit</h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Which metrics are connected · which accounts have gaps
            </p>
          </div>
          {onBack && !embedded ? (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              ← Back
            </button>
          ) : (
            <button
              type="button"
              onClick={fetchAudit}
              disabled={loading}
              aria-busy={loading}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          )}
        </div>

        <div className="border-b border-slate-100 py-4">
          <div className="flex flex-wrap items-center gap-4 rounded-lg border border-indigo-100 bg-gradient-to-br from-violet-50 to-indigo-50 px-4 py-3.5">
            <div className="shrink-0 text-[28px] font-bold leading-none text-violet-700">
              {readinessSummary.readiness}%
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-700">Scoring Accuracy</div>
              <p className="mt-0.5 text-xs text-slate-500">
                {readinessSummary.active} active · {readinessSummary.partial} partial ·{" "}
                {readinessSummary.noData} no data — across{" "}
                {diagnostics?.categories_in_effective_config ?? orderedCategoryCards.length} categories
              </p>
              <p className="mt-0.5 text-[11px] text-slate-400">
                ~{readinessSummary.readiness}% of possible signal captured in current scoring
              </p>
            </div>
            <div className="hidden text-right sm:block">
              <p className="text-[11px] font-medium text-slate-500">Personal visibility preferences</p>
              <p className="mt-0.5 text-[11px] text-slate-400">
                Use <Eye className="inline h-3 w-3 align-text-bottom" /> on each metric row to show/hide in your KPI
                views
              </p>
            </div>
          </div>
        </div>

        <div>
          <div className="mb-4 border-b border-gray-200">
            <TabsComponent
              tabs={AUDIT_VIEW_TABS}
              activeTab={view}
              onTabChange={id => setView(id as CoverageView)}
              variant="tab-underline"
              className="mb-0 whitespace-nowrap"
            />
          </div>

          {view === "gaps" ? (
            <div>
              <h3 className="text-sm font-bold text-slate-900">All metrics by category</h3>
              <p className="mb-3 mt-0.5 text-xs text-slate-500">
                Click to expand a category to see metric-level coverage and status.
              </p>
              <div className="space-y-2">
                {orderedCategoryCards.map(cat => {
                  const isOpen = !!expandedCategories[cat.category_key];
                  const barWidth = getCategoryBarWidth(cat);
                  const hasWeight = cat.weight !== undefined;

                  return (
                    <div key={cat.category_key} className="overflow-hidden rounded-lg border border-slate-200">
                      <button
                        type="button"
                        onClick={() => toggleCategory(cat.category_key)}
                        className="flex w-full items-center gap-2.5 bg-slate-50/80 px-3.5 py-2.5 text-left transition-colors hover:bg-slate-100/80"
                      >
                        {hasWeight ? (
                          <span className="shrink-0 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
                            {Math.round(cat.weight || 0)}%
                          </span>
                        ) : null}
                        <span className="text-[13px] font-semibold text-slate-800">
                          {getCategoryByLabel(cat.category_display_name)?.label ??
                            getCategoryById(cat.category_key)?.label ??
                            cat.category_display_name}
                        </span>
                        <span className="ml-auto hidden text-[11px] sm:inline">
                          <span className="text-emerald-600/80">{cat.activeCt} active</span>
                          {" · "}
                          <span className="text-amber-600/80">{cat.partialCt} partial</span>
                          {" · "}
                          <span className="text-slate-500">{cat.noDataCt} no data</span>
                        </span>
                        <span className="hidden h-1.5 w-[72px] shrink-0 overflow-hidden rounded-full bg-slate-200 sm:inline-flex">
                          <span
                            className="h-full rounded-full bg-gradient-to-r from-emerald-200 to-amber-200"
                            style={{ width: `${barWidth}%` }}
                          />
                        </span>
                        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-slate-400 transition-colors hover:border-slate-300 hover:bg-slate-100 hover:text-slate-600">
                          <ChevronRight
                            className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-90" : ""}`}
                          />
                        </span>
                      </button>

                      {isOpen ? (
                        <div className="border-t border-slate-100">
                          {cat.metrics.map((metric, idx) => {
                            const statusLabel = metric.status_label;
                            const description =
                              METRIC_STATUS_DESCRIPTION[statusLabel] ||
                              METRIC_STATUS_DESCRIPTION.Active;
                            const coverageText = formatCoverageText(metric.raw_coverage_value);

                            return (
                              <div
                                key={metric.metric_id}
                                className={`flex items-center gap-3 px-3.5 py-2 ${
                                  idx < cat.metrics.length - 1 ? "border-b border-slate-100" : ""
                                }`}
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="text-xs font-medium text-slate-700">
                                    {metric.metric_name}
                                  </div>
                                  <div className="text-[10.5px] text-slate-400">{description}</div>
                                </div>
                                <span
                                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusPillClass(statusLabel)}`}
                                >
                                  {statusLabel}
                                </span>
                                <span className="w-16 shrink-0 text-right text-[10.5px] text-slate-400 tabular-nums">
                                  {coverageText}
                                </span>
                                {statusLabel !== "No data" ? (
                                  <button
                                    type="button"
                                    className="shrink-0 rounded p-1 text-slate-400 hover:bg-violet-50 hover:text-violet-600"
                                    title="Toggle personal visibility"
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                  </button>
                                ) : (
                                  <span className="w-7 shrink-0" aria-hidden />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>

              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                <div className="mb-1.5 text-xs font-medium text-slate-700">Status key</div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-slate-500">
                  {STATUS_KEY_ITEMS.map(item => (
                    <span key={item.label} className="inline-flex items-center gap-1.5">
                      <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${item.className}`}>
                        {item.label}
                      </span>
                      {item.hint}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <h3 className="mb-2 text-sm font-bold text-slate-800">Coverage Matrix</h3>
              <div ref={matrixScrollRef} className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-[980px] border-collapse text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th
                        rowSpan={2}
                        className="sticky left-0 z-10 border-b border-r border-slate-200 bg-slate-50 px-3 py-2 text-left text-[11px] font-semibold text-slate-600"
                      >
                        Account
                      </th>
                      {matrixCategoryGroups.map((group, idx) => (
                        <th
                          key={`${group.category}-${idx}`}
                          colSpan={group.count}
                          className="border-b border-r border-slate-200 px-2 py-2 text-center text-[10px] font-bold uppercase tracking-wide text-slate-700"
                        >
                          {group.category}
                        </th>
                      ))}
                    </tr>
                    <tr>
                      {matrixColumns.map(col => (
                        <th
                          key={col.metric_id}
                          className="border-b border-r border-slate-200 bg-white px-1.5 py-2 text-center align-top text-[10px] font-semibold text-slate-700"
                          title={`${col.category_display_name} · ${col.metric_name}`}
                          style={{
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            lineHeight: 1.15,
                            minWidth: isMatrixOverflowing ? "72px" : "96px",
                            maxWidth: isMatrixOverflowing ? "88px" : "120px",
                          }}
                        >
                          {String(col.metric_name || "")
                            .replace(/\s*\(CSAT\)\s*/gi, " (CSAT)")
                            .replace(/\s+/g, " ")
                            .trim()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {metricRollups.map((row: any) => {
                      const metricMap = new Map(
                        (row.metrics || []).map((m: any) => [String(m.metric_id), m])
                      );
                      return (
                        <tr key={row.account_id} className="hover:bg-slate-50/80">
                          <td className="sticky left-0 z-10 border-b border-r border-slate-200 bg-white px-3 py-2 font-medium text-slate-800">
                            {row.account_name || row.account_id}
                          </td>
                          {matrixColumns.map(col => {
                            const cell = metricMap.get(col.metric_id) as
                              | { cell_coverage_percentage?: number }
                              | undefined;
                            const pct = Number(cell?.cell_coverage_percentage || 0);
                            const isComplete = pct === 100;
                            return (
                              <td
                                key={`${row.account_id}-${col.metric_id}`}
                                className="border-b border-r border-slate-200 px-2 py-2 text-center"
                              >
                                <span
                                  className={`inline-flex min-w-[22px] items-center justify-center rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                                    isComplete
                                      ? "bg-emerald-100 text-emerald-700"
                                      : "bg-slate-100 text-slate-500"
                                  }`}
                                  title={`${col.metric_name} (${pct}%)`}
                                >
                                  {isComplete ? "✓" : "—"}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                  Complete
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-slate-400" />
                  Undefined
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataCoverageAuditPanel;
